import {
  createContext,
  useState,
  ReactNode,
  ReactElement,
  useMemo,
  useCallback,
} from 'react'

import { ErrorContextType } from '@types'

import {
  notFoundError,
  internalServerError,
  ErrorDisplayProps,
} from '../constants/error'

const ErrorContext = createContext<ErrorContextType | undefined>(undefined)

const ErrorProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [error, setError] = useState<ErrorDisplayProps | null>(null)
  const [previousUrl, setPreviousUrl] = useState<string | null>(null)

  const showError = useCallback(
    (
      statusCode: 400 | 404 | 500 | null,
      prevUrl?: string | null,
      customError?: ErrorDisplayProps
    ): void => {
      let errorData: ErrorDisplayProps = internalServerError

      if (statusCode) {
        switch (statusCode) {
          case 400:
            errorData = internalServerError
            break
          case 404:
            errorData = notFoundError
            break
          case 500:
            errorData = internalServerError
            break
          default:
            errorData = internalServerError
            break
        }
      } else if (customError) {
        errorData = customError
      }

      setError(errorData)
      setPreviousUrl(
        prevUrl || window.location.pathname + window.location.search
      )
    },
    []
  )

  const clearError = useCallback((): void => {
    setError(null)
    setPreviousUrl(null)
  }, [])

  // Memoize the context value to prevent infinite re-renders
  const contextValue = useMemo(
    () => ({
      error,
      previousUrl,
      showError,
      clearError,
    }),
    [error, previousUrl, showError, clearError]
  )

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
    </ErrorContext.Provider>
  )
}

export { ErrorContext, ErrorProvider }
